import { useParams } from "@tanstack/react-router";
import { PageShell } from "~/components/layout/page-shell";
import { Skeleton } from "~/components/ui/skeleton";

/**
 * Router pending state for symbol detail routes (pendingMs: 0): the app
 * chrome stays live while this paints the page's silhouette inside the
 * outlet during the lazy chunk load. The ticker is real data (it comes from
 * the URL), so it renders as content, not a placeholder — the page is
 * adaptive from its very first frame.
 *
 * Ships in the EAGER route file, not the lazy page chunk — keep imports to
 * layout primitives only.
 */
export function SymbolPagePending() {
  const params = useParams({ strict: false }) as { symbol?: string };
  const displaySymbol = params.symbol?.toUpperCase() ?? null;

  return (
    <PageShell
      className="bg-surface-1 pb-10"
      contentClassName="pt-12"
      padY="none"
      size="wide"
    >
      <PageShell.Main>
        <div className="flex items-center gap-3.5">
          <Skeleton className="size-12 rounded-full" />
          <div>
            {displaySymbol ? (
              <h1 className="text-title-lg font-medium text-foreground">
                {displaySymbol}
              </h1>
            ) : (
              <Skeleton className="h-7 w-28 rounded-full" />
            )}
            <Skeleton className="mt-2 h-3.5 w-40 rounded-full" />
          </div>
        </div>
        <div className="h-[23rem] overflow-hidden rounded-2xl border bg-card p-5 lg:h-[27rem]">
          <Skeleton className="h-8 w-40 rounded-full" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-8 w-24 rounded-full" />
          <Skeleton className="h-8 w-24 rounded-full" />
        </div>
      </PageShell.Main>
      <PageShell.Rail className="space-y-4 max-lg:hidden" width="lg">
        <div className="h-[32rem] page-surface-panel rounded-lg p-5">
          <Skeleton className="h-8 w-40 rounded-full" />
          <Skeleton className="mt-4 h-3.5 rounded-full" />
          <Skeleton className="mt-4 h-3.5 rounded-full" />
          <Skeleton className="mt-4 h-3.5 rounded-full" />
        </div>
      </PageShell.Rail>
    </PageShell>
  );
}
